import { BetaSchemaForm, ProFormInstance } from '@ant-design/pro-form';
import { RowProps, Space, Typography, message } from 'antd';
import { FC, useCallback, useRef, useEffect } from 'react';
import { useQueryClient } from 'react-query';
import { UpdateRoleRequest } from 'services/api/client/src';
import errorHandler from 'utils/errorHandler';
import { useUpdateRole, useRoleById } from 'services/api/useSettings';
import usePermissionStore from '../../hooks';
import EditRoleFields from './EditRoleFields';
import { DataItem, EditRoleProps } from './typings';

const rowProps: RowProps = { gutter: 16 };

const Title: any = () => (
  <Space>
    <Typography.Title level={4} className="mr0">
      Edit Role
    </Typography.Title>
  </Space>
);

const EditRoleModal: FC<EditRoleProps> = ({ modal, selectedRole }) => {
  const queryClient = useQueryClient();
  const updateRole = useUpdateRole(queryClient);
  const selectedRoleData = usePermissionStore((state) => state.selectedRoleData);
  const setSelectedRoleData = usePermissionStore((state) => state.setSelectedRoleData);
  const editableFormRef = useRef<ProFormInstance<DataItem>>();
  const resetFields = useCallback(() => {
    editableFormRef.current?.resetFields();
  }, []);

  const closeModal = useCallback(() => {
    modal.hide();
    resetFields();
    setSelectedRoleData({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal, resetFields]);

  const { data: roleByIdResponse } = useRoleById(selectedRoleData?.id || '');

  useEffect(() => {
    if (roleByIdResponse?.roleName) {
      editableFormRef.current?.setFieldsValue({
        roleValue: String(roleByIdResponse?.roleName),
        permissionValue: roleByIdResponse?.permissions,
      });
    } else {
      editableFormRef.current?.setFieldsValue({
        roleValue: undefined,
        permissionValue: undefined,
      });
    }
  }, [roleByIdResponse?.permissions, roleByIdResponse?.roleName]);

  return (
    <BetaSchemaForm<DataItem>
      formRef={editableFormRef}
      layoutType="ModalForm"
      columns={EditRoleFields()}
      grid
      // @ts-ignore
      title={<Title />}
      visible={modal.visible}
      rowProps={rowProps}
      autoFocusFirstInput
      modalProps={{
        destroyOnClose: false,
        width: '40%',
        ...modal,
        centered: true,
        onCancel: closeModal,
      }}
      submitter={{
        searchConfig: {
          submitText: 'Save',
        },
        submitButtonProps: {
          shape: 'round',
          style: {
            color: '#0a5f7a',
            background: '#bcd530',
            border: '1px solid #bcd530',
          },
        },
        resetButtonProps: {
          shape: 'round',
          type: 'primary',
          ghost: true,
        },
        render: (_, defaultDoms) => defaultDoms,
      }}
      submitTimeout={2000}
      onFinish={async (value: any) => {
        try {
          const reqData: UpdateRoleRequest = {
            roleName: value.roleValue,
            permission: value.permissionValue,
            roleId: selectedRole?.id,
          };
          await updateRole.mutateAsync({
            params: reqData,
          });
          message.success('Successfully Updated Role');
          closeModal();
        } catch (error: any) {
          if (error?.response?.bodyAsText) {
            const errorJson = JSON.parse(error.response.bodyAsText);
            if (errorJson?.status === 403) {
              message.warning(
                'A role with the same name or permissions already exists. Please select different name or permissions to create a role',
              );
            } else {
              message.error(errorHandler(error));
            }
          } else {
            message.error(errorHandler(error));
          }
        }
      }}
    />
  );
};
export default EditRoleModal;
